import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { areYouSure } from 'lib/utils/toast';
import { Button, Col, Image, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';

import { anchovy } from 'lib/css/colors';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import RecommendedImagesTabView from 'components/l4_default_images/recommendations/RecommendedImagesTabView';
import {
  TAXONOMY_SET_L3_DEFAULT_IMAGE,
  TAXONOMY_SET_L3_DEFAULT_IMAGE_NOT_APPLICABLE,
} from 'lib/networking/endpoints';
import usePost from 'lib/hooks/usePost';

function L3DefaultImageSection({
  selectedL3CategoryId,
  setSelectedL3CategoryId,
  selectedThumbnailUrl,
  setSelectedThumbnailUrl,
  selectedL3CategoryName,
  setSelectedL3CategoryName,
  L3Category,
  l3CategoryOptions,
  loading,
  refetchCategories,
  setL3Search,
}) {
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    setUploadedImages([]);
  }, [selectedL3CategoryId]);

  const { postData: markTooBroad } = usePost(TAXONOMY_SET_L3_DEFAULT_IMAGE_NOT_APPLICABLE, () => {
    Swal.fire({
      icon: 'success',
      text: 'Default image state updated as too broad',
      title: 'Success',
    }).then(() => goToNextCategory());
  });

  const { postData: setL3DefaultImage, loading: updating } = usePost(
    TAXONOMY_SET_L3_DEFAULT_IMAGE,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Default Image updated successfully',
        title: 'Success',
      }).then(() => {
        const currentIndex = l3CategoryOptions.findIndex(
          option => option.id === selectedL3CategoryId,
        );
        const nextIndex = (currentIndex + 1) % l3CategoryOptions.length;
        setSelectedL3CategoryName(l3CategoryOptions[nextIndex]?.label);
        setSelectedL3CategoryId(l3CategoryOptions[nextIndex]?.id);
        refetchCategories();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while updating default image: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const categories = [
    { label: 'L1 Category', name: L3Category?.l1_category_name },
    { label: 'L2 Category', name: L3Category?.l2_category_name },
  ];

  const goToNextCategory = () => {
    const currentIndex = l3CategoryOptions.findIndex(option => option.id === selectedL3CategoryId);
    const nextIndex = (currentIndex + 1) % l3CategoryOptions.length;
    setSelectedL3CategoryName(l3CategoryOptions[nextIndex]?.label);
    setSelectedL3CategoryId(l3CategoryOptions[nextIndex]?.id);
    setL3Search(l3CategoryOptions[nextIndex]?.label);
  };

  if (loading) {
    return <LoadingSpinner short />;
  }
  return (
    <div>
      <Row className="mb-3">
        <Col>
          <Row>
            <Col>
              <h2>
                <strong>L3: {selectedL3CategoryName}</strong>
              </h2>
            </Col>
          </Row>
          {selectedL3CategoryName ? (
            <Row>
              {categories.map((category, index) => (
                <Col key={index}>
                  <strong>{`${category.label} : ${category.name}`}</strong>
                </Col>
              ))}
            </Row>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={4}>
          <Image
            className={css(styles.square)}
            fluid
            src={selectedThumbnailUrl ?? placeholderProductImage}
          />
        </Col>
        <Col>
          <Row className="my-4">
            <Col>
              {L3Category?.default_image_uploaded_by ? (
                <strong>{`Image uploaded by : ${L3Category?.default_image_uploaded_by}`}</strong>
              ) : null}
            </Col>
          </Row>
          <Row className="my-4">
            <Col>
              {L3Category?.feedback ? (
                <strong>{`Feedback : ${L3Category?.feedback}`}</strong>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="my-4">
        <Col>
          <Button
            className="mr-3"
            variant="outline-primary"
            onClick={() => {
              setL3DefaultImage({
                l3_category_id: selectedL3CategoryId,
                default_image_url: selectedThumbnailUrl,
              });
              setUploadedImages([]);
            }}
            size="lg"
            disabled={updating || !selectedL3CategoryId || !selectedThumbnailUrl}
          >
            Save Changes
          </Button>
          <Button
            className="mr-3"
            variant="outline-primary"
            onClick={() => {
              const currentIndex = l3CategoryOptions.findIndex(
                option => option.id === selectedL3CategoryId,
              );
              const previousIndex =
                (currentIndex - 1 + l3CategoryOptions.length) % l3CategoryOptions.length;
              setSelectedL3CategoryName(l3CategoryOptions[previousIndex]?.label);
              setSelectedL3CategoryId(l3CategoryOptions[previousIndex]?.id);
              setL3Search(l3CategoryOptions[previousIndex]?.label);
            }}
            size="lg"
            disabled={updating || !selectedL3CategoryId}
          >
            Previous
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => goToNextCategory()}
            size="lg"
            disabled={updating || !selectedL3CategoryId}
          >
            Next
          </Button>
          <Button
            disabled={updating || !selectedL3CategoryId}
            className="ml-3"
            onClick={() =>
              areYouSure(() => {
                markTooBroad({
                  l3_category_id: selectedL3CategoryId,
                });
              }, 'Mark this L3 category as too broad to have an image?')
            }
          >
            Too Broad
          </Button>
        </Col>
      </Row>
      <hr />
      <RecommendedImagesTabView
        l4CategoryId={selectedL3CategoryId}
        uploadedImages={uploadedImages}
        setUploadedImages={setUploadedImages}
        selectedThumbnailUrl={selectedThumbnailUrl}
        setSelectedThumbnailUrl={setSelectedThumbnailUrl}
        l3Category={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  modalHeader: {
    borderBottom: 'none',
  },
  recommendationsContainer: {
    height: '420px',
    overflowY: 'auto',
  },
  square: {
    width: '250px',
    height: '250px',
    objectFit: 'contain',
    border: '1px solid ' + anchovy,
    marginBottom: '0.75rem',
    padding: '0.2rem',
  },
});

L3DefaultImageSection.propTypes = {
  selectedL3CategoryId: PropTypes.number,
  setSelectedL3CategoryId: PropTypes.func,
  selectedThumbnailUrl: PropTypes.string,
  setSelectedThumbnailUrl: PropTypes.func,
  selectedL3CategoryName: PropTypes.string,
  setSelectedL3CategoryName: PropTypes.func,
  L3Category: PropTypes.object,
  l3CategoryOptions: PropTypes.array,
  loading: PropTypes.bool,
  refetchCategories: PropTypes.func,
  setL3Search: PropTypes.func,
};

export default L3DefaultImageSection;
